import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import authService from '../components/api-authorization/AuthorizeService';
import { connectivityMonitor } from './connectivityMonitor';
import { EventVm, MatchHubMessageTypeEnum } from '../utils/api';

/**
 * Callbacks a livescore screen provides to react to remote event mutations
 * broadcast by the backend MatchHub (topic "Event", group = matchId).
 */
export interface MatchEventsHubHandlers {
    onRemoteCreate: (event: EventVm) => void;
    onRemoteUpdate: (event: EventVm) => void;
    onRemoteDelete: (event: EventVm) => void;
    /** Full events + match refetch; called after the initial join and every reconnect. */
    onResync: () => Promise<void>;
}

/** After the built-in 0s / 2s / 10s / 30s retries, keep trying at this pace. */
/** Game screens cap every retry at 2s. The policy never returns null. */
export const HUB_RECONNECT_DELAY_MS = 2000;

export const SLOW_RECONNECT_MS = HUB_RECONNECT_DELAY_MS;

/** Abort negotiate and the other hub HTTP calls. Default in the client is 100s. */
export const HUB_HTTP_TIMEOUT_MS = 2500;

/** First retry is immediate. Later retries stay at the cap. Never stops. */
export const gameHubRetryDelay = (previousRetryCount: number): number =>
    previousRetryCount <= 0 ? 0 : HUB_RECONNECT_DELAY_MS;

/**
 * One connection per mounted livescore screen: connect -> JoinGroup(matchId)
 * -> listen on "Event" -> dispatch to handlers. Auto-reconnect re-joins the
 * group and triggers a full resync so nothing missed while offline is lost.
 * When those default retries run out, or the first start() fails, a slow
 * timer keeps calling start() until the screen unmounts.
 * Game screens cap that wait and never stop retrying. Negotiate aborts on
 * HUB_HTTP_TIMEOUT_MS so a dead socket cannot block pings. The browser
 * online event restarts start() at once. That event does not paint the badge.
 */
export class MatchEventsHub {
    private connection: HubConnection | null = null;

    private matchId: number | null = null;

    private handlers: MatchEventsHubHandlers | null = null;

    private intentionalStop = false;

    private slowTimer: ReturnType<typeof setTimeout> | null = null;

    private unsubRecovery: (() => void) | null = null;

    /** In-flight start() promise. Online recovery stops it so negotiate cannot sit. */
    private starting: Promise<void> | null = null;

    /** Ignores onclose while online recovery is calling stop(). */
    private restarting = false;

    /** Stale start() after a recovery must not clear the new connection. */
    private startEpoch = 0;

    public connectAndJoin = async (
        matchId: number,
        handlers: MatchEventsHubHandlers
    ): Promise<void> => {
        this.intentionalStop = false;
        this.clearSlowTimer();
        this.matchId = matchId;
        this.handlers = handlers;

        const connection = new HubConnectionBuilder()
            .withUrl('/hubs/match', {
                accessTokenFactory: async (): Promise<string> =>
                    (await authService.getAccessToken()) || '',
                timeout: HUB_HTTP_TIMEOUT_MS,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext): number =>
                    gameHubRetryDelay(retryContext.previousRetryCount),
            })
            .configureLogging(LogLevel.Warning)
            .build();

        connection.on('Event', this.handleMessage);
        connection.onreconnecting((): void => {
            connectivityMonitor.setHubPhase('reconnecting');
        });
        connection.onreconnected(async (): Promise<void> => {
            connectivityMonitor.setHubPhase('connected');
            try {
                await this.joinGroup();
                await this.handlers?.onResync();
            } catch {
                // Next reconnect cycle will retry; live messages resume regardless.
            }
        });
        connection.onclose((): void => {
            connectivityMonitor.setHubPhase('disconnected');
            if (this.intentionalStop || this.restarting) return;
            this.scheduleSlowReconnect();
        });

        this.connection = connection;
        this.unsubRecovery?.();
        this.unsubRecovery = connectivityMonitor.subscribeRecovery(() => {
            void this.reconnectNow();
        });
        await this.startAndJoin();
    };

    public disconnect = async (): Promise<void> => {
        this.intentionalStop = true;
        this.restarting = false;
        this.starting = null;
        this.unsubRecovery?.();
        this.unsubRecovery = null;
        this.clearSlowTimer();
        const { connection, matchId } = this;
        this.connection = null;
        this.handlers = null;
        this.matchId = null;
        connectivityMonitor.setHubPhase('disconnected');
        if (!connection) return;
        try {
            if (connection.state === HubConnectionState.Connected && matchId !== null) {
                await connection.invoke('LeaveGroup', matchId.toString());
            }
            await connection.stop();
        } catch {
            // Ignore teardown errors (e.g. already disconnected).
        }
    };

    private startAndJoin = async (): Promise<void> => {
        const connection = this.connection;
        const handlers = this.handlers;
        const epoch = this.startEpoch;
        if (!connection || !handlers || this.intentionalStop) return;
        try {
            if (connection.state === HubConnectionState.Disconnected) {
                connectivityMonitor.setHubPhase('reconnecting');
                const pending = connection.start();
                this.starting = pending;
                try {
                    await pending;
                } finally {
                    if (this.starting === pending) this.starting = null;
                }
            }
            // Unmount can win the race with start(). Do not join or resync after that.
            if (this.intentionalStop || this.connection !== connection || epoch !== this.startEpoch) {
                return;
            }
            await this.joinGroup();
            if (this.intentionalStop || this.connection !== connection || epoch !== this.startEpoch) {
                return;
            }
            connectivityMonitor.setHubPhase('connected');
            await handlers.onResync();
        } catch {
            if (epoch !== this.startEpoch || this.intentionalStop) return;
            connectivityMonitor.setHubPhase('disconnected');
            this.scheduleSlowReconnect();
        }
    };

    /**
     * Drop a hung negotiate and call start() now. A connected hub is left alone.
     */
    private reconnectNow = async (): Promise<void> => {
        if (this.intentionalStop || !this.handlers || !this.connection || this.restarting) return;
        const connection = this.connection;
        if (!this.starting && connection.state === HubConnectionState.Connected) return;
        this.restarting = true;
        this.startEpoch += 1;
        this.clearSlowTimer();
        try {
            if (this.starting || connection.state !== HubConnectionState.Disconnected) {
                await connection.stop();
            }
        } catch {
            // stop() rejects the in-flight start. The epoch check drops that attempt.
        }
        this.restarting = false;
        if (this.intentionalStop || this.connection !== connection) return;
        await this.startAndJoin();
    };

    private scheduleSlowReconnect = (): void => {
        if (this.intentionalStop || this.restarting || !this.handlers || this.slowTimer) return;
        this.slowTimer = setTimeout(() => {
            this.slowTimer = null;
            void this.startAndJoin();
        }, SLOW_RECONNECT_MS);
    };

    private clearSlowTimer = (): void => {
        if (!this.slowTimer) return;
        clearTimeout(this.slowTimer);
        this.slowTimer = null;
    };

    private joinGroup = async (): Promise<void> => {
        if (!this.connection || this.matchId === null) return;
        await this.connection.invoke('JoinGroup', this.matchId.toString());
    };

    private handleMessage = (message: {
        messageType: MatchHubMessageTypeEnum;
        event?: EventVm;
    }): void => {
        const handlers = this.handlers;
        if (!handlers || !message?.event) return;
        const event = EventVm.fromJS(message.event);
        switch (message.messageType) {
            case MatchHubMessageTypeEnum.CreateEvent:
                handlers.onRemoteCreate(event);
                break;
            case MatchHubMessageTypeEnum.UpdateEvent:
                handlers.onRemoteUpdate(event);
                break;
            case MatchHubMessageTypeEnum.DeleteEvent:
                handlers.onRemoteDelete(event);
                break;
            case MatchHubMessageTypeEnum.MatchUpdated:
                // Lineup, officials, venue, and the other non-event saves.
                // Refetch the match. Do not copy Event.Match onto local state:
                // that stub is only id and status, and this is not MatchFinished.
                void handlers.onResync();
                break;
            default:
                // MatchFinished is the status-change signal. This screen does not
                // finish the match from the message. A later refetch loads the status.
                break;
        }
    };
}

export const applyRemoteDelete = (events: EventVm[], event: EventVm): EventVm[] =>
    events.filter((e) => e.id !== event.id);
